import type { FormSchemaResponse } from "./schema";

type DraftValue = string | boolean | string[];

const SAVE_DELAY_MS = 250;
const DRAFT_TOKEN_KEY = "sanctum-forms:draft-token:v1";

export interface DraftPersistence {
  clear(): void;
  dispose(): void;
  indicators: SaveIndicators | null;
}

export interface SaveIndicators {
  markUnsaved(fieldName: string): void;
  markSaving(fieldName: string): void;
  markSaved(fieldName: string): void;
}

export function initSaveIndicators(form: HTMLFormElement): SaveIndicators {
  let focusedField: string | null = null;
  const LABELS: Record<string, string> = {
    unsaved: "Unsaved changes",
    saving: "Saving\u2026",
    saved: "Saved",
  };

  function getStatusEl(fieldName: string): HTMLElement | null {
    return form.querySelector(
      `[data-field-id="${cssEscape(fieldName)}"] [part="save-status"]`,
    );
  }

  function setState(fieldName: string, state: string): void {
    const el = getStatusEl(fieldName);
    if (el) {
      const wasVisible = el.classList.contains("visible");
      el.className = `state-${state}`;
      if (wasVisible) el.classList.add("visible");
      const lbl = el.querySelector('[part="save-label"]');
      if (lbl) lbl.textContent = LABELS[state];
    }
  }

  form.querySelectorAll("[data-field-id]").forEach((wrapper) => {
    const fieldName = (wrapper as HTMLElement).dataset.fieldId;
    if (!fieldName) return;
    const control = form.querySelector<HTMLElement>(
      `[name="${cssEscape(fieldName)}"]`,
    );
    if (!control) return;
    control.addEventListener("focus", () => {
      focusedField = fieldName;
      form
        .querySelectorAll('[part="save-status"]')
        .forEach((el) => el.classList.remove("visible"));
      const target = getStatusEl(fieldName);
      if (target) target.classList.add("visible");
    });
    control.addEventListener("blur", () => {
      setTimeout(() => {
        if (
          !document.activeElement ||
          !form.contains(document.activeElement)
        ) {
          focusedField = null;
          form
            .querySelectorAll('[part="save-status"]')
            .forEach((el) => el.classList.remove("visible"));
        }
      }, 150);
    });
  });

  return {
    markUnsaved(fieldName: string) {
      setState(fieldName, "unsaved");
    },
    markSaving(fieldName: string) {
      setState(fieldName, "saving");
    },
    markSaved(fieldName: string) {
      setState(fieldName, "saved");
    },
  };
}

function getDraftUrl(baseUrl: string, endpointId: string): string {
  return `${baseUrl.replace(/\/$/, "")}/submit/${endpointId}/draft`;
}

function getDraftToken(schema: FormSchemaResponse): string {
  if (schema.draft_token) {
    try {
      localStorage.setItem(
        `${DRAFT_TOKEN_KEY}:${schema.endpoint_id ?? ""}`,
        schema.draft_token,
      );
    } catch {
      // best-effort
    }
    return schema.draft_token;
  }
  try {
    const stored = localStorage.getItem(
      `${DRAFT_TOKEN_KEY}:${schema.endpoint_id ?? ""}`,
    );
    if (stored) return stored;
  } catch {
    // best-effort
  }
  return "";
}

function buildHeaders(schema: FormSchemaResponse): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getDraftToken(schema);
  if (token) {
    headers["X-Draft-Token"] = token;
  }
  return headers;
}

async function fetchDraft(
  baseUrl: string,
  endpointId: string,
  schema: FormSchemaResponse,
): Promise<Record<string, unknown>> {
  try {
    const response = await fetch(getDraftUrl(baseUrl, endpointId), {
      method: "GET",
      headers: buildHeaders(schema),
    });
    if (!response.ok) return {};
    const body = (await response.json()) as { payload: Record<string, unknown> };
    return body.payload ?? {};
  } catch {
    return {};
  }
}

async function putDraft(
  baseUrl: string,
  endpointId: string,
  schema: FormSchemaResponse,
  payload: Record<string, DraftValue>,
): Promise<void> {
  try {
    await fetch(getDraftUrl(baseUrl, endpointId), {
      method: "PUT",
      headers: buildHeaders(schema),
      body: JSON.stringify(payload),
    });
  } catch {
    // Draft save is best-effort
  }
}

async function deleteDraftServer(
  baseUrl: string,
  endpointId: string,
  schema: FormSchemaResponse,
): Promise<void> {
  try {
    await fetch(getDraftUrl(baseUrl, endpointId), {
      method: "DELETE",
      headers: buildHeaders(schema),
    });
  } catch {
    // Best-effort
  }
}

export function initDraftPersistence(
  form: HTMLFormElement,
  schema: FormSchemaResponse,
  baseUrl: string,
  endpointId: string,
): DraftPersistence {
  const allowedNames = new Set(
    schema.field_schema
      .filter(
        (field) => field.type !== "hidden" && field.type !== "password" && field.type !== "file" && field.name !== schema.settings.honeypot_field_name,
      )
      .map((field) => field.name),
  );
  const indicators = initSaveIndicators(form);
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let lastChangedField: string | null = null;

  // Restore draft from server on init
  fetchDraft(baseUrl, endpointId, schema).then((serverDraft) => {
    restoreDraft(form, serverDraft, allowedNames);
  });

  const scheduleSave = (changedField?: string | null) => {
    lastChangedField = changedField ?? null;
    if (saveTimer) clearTimeout(saveTimer);
    if (changedField) indicators?.markUnsaved(changedField);
    const field = lastChangedField;
    saveTimer = setTimeout(() => {
      saveTimer = null;
      if (field) indicators?.markSaving(field);
      const payload = collectDraft(form, allowedNames);
      putDraft(baseUrl, endpointId, schema, payload);
      if (field) indicators?.markSaved(field);
    }, SAVE_DELAY_MS);
  };

  const onInput = (e: Event) => {
    const target = e.target as HTMLElement;
    const fieldName = target.getAttribute("name");
    scheduleSave(fieldName);
  };

  form.addEventListener("input", onInput);
  form.addEventListener("change", onInput);

  const dispose = () => {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    form.removeEventListener("input", onInput);
    form.removeEventListener("change", onInput);
  };

  return {
    clear() {
      dispose();
      deleteDraftServer(baseUrl, endpointId, schema);
    },
    dispose,
    indicators,
  };
}

function cssEscape(value: string): string {
  if (
    typeof (globalThis as { CSS?: { escape?: (s: string) => string } }).CSS
      ?.escape === "function"
  ) {
    return (globalThis as { CSS: { escape: (s: string) => string } }).CSS.escape(
      value,
    );
  }
  return value.replace(/"/g, '\\"');
}

function getDraftControls(
  form: HTMLFormElement,
  allowedNames: Set<string>,
): Array<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> {
  return Array.from(form.elements).filter(
    (el): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement => {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) {
        return false;
      }
      if (!el.name || !allowedNames.has(el.name) || el.dataset.honeypot === "true") return false;
      if (el.disabled) return false;
      if (!(el instanceof HTMLSelectElement) && el.readOnly) return false;
      if (el instanceof HTMLInputElement) {
        if (el.type === "password" || el.type === "file") return false;
        return ["text", "email", "tel", "checkbox", "number", "date", "time",
                "datetime-local", "url", "color", "range", "radio", "hidden"].includes(el.type);
      }
      return true;
    },
  );
}

function collectDraft(form: HTMLFormElement, allowedNames: Set<string>): Record<string, DraftValue> {
  const draft: Record<string, DraftValue> = {};
  for (const control of getDraftControls(form, allowedNames)) {
    if (control instanceof HTMLInputElement && control.type === "radio") {
      if (control.checked) draft[control.name] = control.value;
    } else if (control instanceof HTMLInputElement && control.type === "checkbox") {
      const sameName = getDraftControls(form, allowedNames).filter(
        (el): el is HTMLInputElement => el instanceof HTMLInputElement && el.type === "checkbox" && el.name === control.name,
      );
      if (sameName.length > 1) {
        draft[control.name] = sameName.filter((el) => el.checked).map((el) => el.value);
      } else {
        draft[control.name] = control.checked;
      }
    } else if (control instanceof HTMLSelectElement && control.multiple) {
      const selected = Array.from(control.selectedOptions).map((o) => o.value);
      draft[control.name] = selected;
    } else {
      draft[control.name] = control.value;
    }
  }
  return draft;
}

function restoreDraft(
  form: HTMLFormElement,
  values: Record<string, unknown>,
  allowedNames: Set<string>,
): void {
  for (const control of getDraftControls(form, allowedNames)) {
    const value = values[control.name];
    if (control instanceof HTMLInputElement && control.type === "radio") {
      if (typeof value === "string" && control.value === value) control.checked = true;
    } else if (control instanceof HTMLInputElement && control.type === "checkbox") {
      if (typeof value === "boolean") control.checked = value;
      if (Array.isArray(value)) control.checked = value.filter((v): v is string => typeof v === "string").includes(control.value);
    } else if (control instanceof HTMLSelectElement && control.multiple) {
      if (Array.isArray(value)) {
        const set = new Set(value.filter((v): v is string => typeof v === "string"));
        for (const opt of Array.from(control.options)) {
          opt.selected = set.has(opt.value);
        }
      }
    } else if (typeof value === "string") {
      control.value = value;
    }
  }
  form.dispatchEvent(new Event("sanctum:conditional-refresh", { bubbles: true }));

  for (const ol of form.querySelectorAll<HTMLElement>('div[part="ordered-list"]')) {
    ol.dispatchEvent(new Event("sanctum:ordered-list-restore", { bubbles: true }));
  }
}
